import { faChevronDown } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { keyBy } from "lodash";
import { useState } from "react";
import { useFormContext } from "react-hook-form";
import { Button, Divider, Segment } from "semantic-ui-react";
import Tooltip from "../../../../../../components/Tooltip";
import { useNotifyNotificationInfo } from "../../../../hooks/useNotifyNotificationInfo";
import { useNotifySubscriptionGroups } from "../../../../hooks/useNotifySubscriptionGroups";
import { NotifyNotificationsFormData } from "../components/NotifyNotificationsForm";
import classNames from "classnames";

export function NotifyNotificationEstimatedReach({ hasTransparentBackground }: { hasTransparentBackground?: boolean }) {
  const [isOpen, setIsOpen] = useState(false);

  const { watch } = useFormContext<NotifyNotificationsFormData>();
  const {
    channels,
    geoTargetFeatures,
    subscriptionGroups: selectedSubscriptionGroups,
  } = watch(["channels", "geoTargetFeatures", "subscriptionGroups"]);

  const { notificationInfo, isNotificationInfoLoading } = useNotifyNotificationInfo({
    body: {
      channels: channels.active,
      geoTargeting: geoTargetFeatures
        ? {
            featureIds: geoTargetFeatures.featureIds,
            restrictToGeoTargetBoundary: geoTargetFeatures.restrictBoundary,
          }
        : null,
      subscriptionGroupIds: selectedSubscriptionGroups.map((group) => group.value),
      voiceForceSendAll: channels.additionalData.voiceForceSendAll,
    },
  });

  const { subscriptionGroups, isSubscriptionGroupsLoading } = useNotifySubscriptionGroups();
  const groupsById = keyBy(subscriptionGroups || [], "id");

  const isLoading = isNotificationInfoLoading || isSubscriptionGroupsLoading;

  const totalRecipients = notificationInfo?.estimatedReach.totalRecipients ?? 0;
  const totalNotifications = notificationInfo?.estimatedReach.totalNotifications ?? 0;

  const targetingSubscriptionGroups = notificationInfo?.estimatedReach.targetingReach.subscriptionGroups || [];
  const geoTargetingAddedBoundary = notificationInfo?.estimatedReach.targetingReach.geoTargetingAddedBoundary || null;
  const showGeoTargetingAddedBoundary = geoTargetingAddedBoundary !== null;

  const pushChannelReach = notificationInfo?.estimatedReach.channelReach.breakdown.push || 0;
  const showPushChannelReach = pushChannelReach > 0;
  const emailChannelReach = notificationInfo?.estimatedReach.channelReach.breakdown.email || 0;
  const showEmailChannelReach = emailChannelReach > 0;
  const smsChannelReach = notificationInfo?.estimatedReach.channelReach.breakdown.sms || 0;
  const showSmsChannelReach = smsChannelReach > 0;
  const voiceChannelReach = notificationInfo?.estimatedReach.channelReach.breakdown.voice || 0;
  const showVoiceChannelReach = voiceChannelReach > 0;

  const showChannelReachSection =
    showPushChannelReach || showEmailChannelReach || showSmsChannelReach || showVoiceChannelReach;

  return (
    <Segment
      className={classNames("tw-m-0 tw-flex-shrink-0 tw-border-border tw-bg-[#EEF1F3] tw-p-0", {
        "tw-border-none tw-bg-transparent": hasTransparentBackground,
      })}
      loading={isLoading}
    >
      <div
        className={classNames("tw-flex tw-items-end tw-justify-between tw-p-gg-5", {
          "tw-px-0": hasTransparentBackground,
        })}
      >
        <div role='button' tabIndex={-1} className='tw-cursor-pointer' onClick={() => setIsOpen((prev) => !prev)}>
          <h2 className='tw-m-0 tw-text-[12px] tw-font-bold tw-capitalize tw-tracking-[0.84px] tw-text-text-hint'>
            ESTIMATED REACH
            <Tooltip className='tw-text-text-hint'>
              These numbers are estimates and the true counts are refreshed upon sending
            </Tooltip>
          </h2>
          <h3 className='tw-mt-2 tw-text-[16px] tw-font-black'>
            {totalRecipients?.toLocaleString("en-US")} recipients <span className='tw-mx-1 tw-font-thin'>•</span>{" "}
            {totalNotifications?.toLocaleString("en-US")} notifications
          </h3>
        </div>
        <Button className='button-ghost black icon tw-m-0' onClick={() => setIsOpen((prev) => !prev)}>
          {isOpen ? <span className='tw-sr-only'>Collapse</span> : <span className='tw-sr-only'>Expand</span>}
          {isOpen ? <FontAwesomeIcon icon={faChevronDown} rotation={180} /> : <FontAwesomeIcon icon={faChevronDown} />}
        </Button>
      </div>
      {isOpen && (
        <div
          className={classNames("tw-p-gg-4 tw-pt-0", {
            "tw-px-0": hasTransparentBackground,
          })}
        >
          <Segment className='tw-p-gg-4'>
            <div>
              <h3 className='tw-text-lg tw-font-bold'>Targeting</h3>
              <div className='tw-ml-gg-3'>
                <div className='tw-space-y-gg-2'>
                  {targetingSubscriptionGroups.map(({ id, count }) => {
                    const group = groupsById[id];
                    return (
                      <div key={id} className='tw-flex tw-justify-between'>
                        <div className='tw-m-0 tw-text-base tw-font-semibold tw-text-text-hint'>{group?.name}</div>
                        <div className='tw-m-0 tw-text-base tw-font-semibold tw-text-text-hint'>
                          {count?.toLocaleString("en-US")}
                        </div>
                      </div>
                    );
                  })}
                  {showGeoTargetingAddedBoundary && (
                    <div className='tw-flex tw-justify-between'>
                      <div className='tw-m-0 tw-text-base tw-font-semibold tw-text-text-hint'>
                        Geo-Target (added boundary)
                      </div>
                      <div className='tw-m-0 tw-text-base tw-font-semibold tw-text-text-hint'>
                        {geoTargetingAddedBoundary?.toLocaleString("en-US")}
                      </div>
                    </div>
                  )}
                </div>
                <Divider className='tw-my-gg-2' />
                <div className='tw-flex tw-justify-between'>
                  <div className='tw-text-base tw-font-semibold'>Unique Users</div>
                  <div className='tw-text-base tw-font-semibold'>{totalRecipients?.toLocaleString("en-US")}</div>
                </div>
              </div>
            </div>
            {showChannelReachSection && (
              <div className='tw-mt-gg-5'>
                <h3 className='tw-text-lg tw-font-bold'>Channel Reach</h3>
                <div className='tw-ml-gg-3'>
                  <div className='tw-space-y-gg-2'>
                    {showPushChannelReach && (
                      <div className='tw-flex tw-justify-between'>
                        <div className='tw-m-0 tw-text-base tw-font-semibold tw-text-text-hint'>Push Notifications</div>
                        <div className='tw-m-0 tw-text-base tw-font-semibold tw-text-text-hint'>
                          {pushChannelReach?.toLocaleString("en-US")}
                        </div>
                      </div>
                    )}
                    {showEmailChannelReach && (
                      <div className='tw-flex tw-justify-between'>
                        <div className='tw-m-0 tw-text-base tw-font-semibold tw-text-text-hint'>Email</div>
                        <div className='tw-m-0 tw-text-base tw-font-semibold tw-text-text-hint'>
                          {emailChannelReach?.toLocaleString("en-US")}
                        </div>
                      </div>
                    )}
                    {showSmsChannelReach && (
                      <div className='tw-flex tw-justify-between'>
                        <div className='tw-m-0 tw-text-base tw-font-semibold tw-text-text-hint'>SMS</div>
                        <div className='tw-m-0 tw-text-base tw-font-semibold tw-text-text-hint'>
                          {smsChannelReach?.toLocaleString("en-US")}
                        </div>
                      </div>
                    )}
                    {showVoiceChannelReach && (
                      <div className='tw-flex tw-justify-between'>
                        <div className='tw-m-0 tw-text-base tw-font-semibold tw-text-text-hint'>Voice</div>
                        <div className='tw-m-0 tw-text-base tw-font-semibold tw-text-text-hint'>
                          {voiceChannelReach?.toLocaleString("en-US")}
                        </div>
                      </div>
                    )}
                  </div>
                  <Divider className='tw-my-gg-2' />
                  <div className='tw-flex tw-justify-between'>
                    <div className='tw-text-base tw-font-semibold'>Total Reach</div>
                    <div className='tw-text-base tw-font-semibold'>
                      {totalNotifications?.toLocaleString("en-US")} notifications
                    </div>
                  </div>
                </div>
              </div>
            )}
          </Segment>
        </div>
      )}
    </Segment>
  );
}
